/**
 * Data Connectors Page (System)
 * ERP and data integrations
 */
import React from 'react';
import { 
  Settings, 
  CheckCircle2, 
  AlertTriangle,
  Clock,
  Plus,
  RefreshCw,
  Database,
  Cloud
} from 'lucide-react';

const CONNECTORS = [
  {
    id: 1,
    name: 'SAP S/4HANA',
    type: 'ERP',
    status: 'connected',
    lastSync: '2025-01-15T10:30:00',
    dataPoints: 1250,
    description: 'Energy consumption, production data',
  },
  {
    id: 2,
    name: 'Microsoft Azure',
    type: 'Cloud',
    status: 'connected',
    lastSync: '2025-01-15T09:15:00',
    dataPoints: 890,
    description: 'Document storage, user management',
  },
  {
    id: 3,
    name: 'Salesforce',
    type: 'CRM',
    status: 'warning',
    lastSync: '2025-01-14T16:45:00',
    dataPoints: 456,
    description: 'Stakeholder engagement data',
  },
  {
    id: 4,
    name: 'Workday',
    type: 'HR',
    status: 'connected',
    lastSync: '2025-01-15T08:00:00',
    dataPoints: 2100,
    description: 'Employee data, diversity metrics',
  },
  {
    id: 5,
    name: 'EcoVadis',
    type: 'Third Party',
    status: 'pending',
    lastSync: null,
    dataPoints: 0,
    description: 'Supplier ESG ratings',
  },
];

const STATUS_CONFIG = {
  connected: { label: 'Connected', bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle2 },
  warning: { label: 'Warning', bg: 'bg-amber-500/20', text: 'text-amber-400', icon: AlertTriangle },
  pending: { label: 'Pending Setup', bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]', icon: Clock },
  error: { label: 'Error', bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertTriangle },
};

const TYPE_ICONS = {
  ERP: Database,
  Cloud: Cloud,
  CRM: Database,
  HR: Database,
  'Third Party': Settings,
};

export default function DataConnectorsPage() {
  const connectedCount = CONNECTORS.filter(c => c.status === 'connected').length;
  const totalDataPoints = CONNECTORS.reduce((sum, c) => sum + c.dataPoints, 0);

  return (
    <div className="space-y-6" data-testid="data-connectors-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Data Connectors</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Manage ERP integrations and data source connections
          </p>
        </div>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
          data-testid="add-connector-btn"
        >
          <Plus size={16} />
          <span className="text-sm font-medium">Add Connector</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <CheckCircle2 size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-400">{connectedCount}</div>
              <div className="text-xs text-[#6B6B6B]">Connected</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Settings size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{CONNECTORS.length}</div>
              <div className="text-xs text-[#6B6B6B]">Total Connectors</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Database size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalDataPoints.toLocaleString()}</div>
              <div className="text-xs text-[#6B6B6B]">Data Points</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <AlertTriangle size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-400">
                {CONNECTORS.filter(c => c.status === 'warning').length}
              </div>
              <div className="text-xs text-[#6B6B6B]">Needs Attention</div>
            </div>
          </div>
        </div>
      </div>

      {/* Connectors List */}
      <div className="space-y-3">
        {CONNECTORS.map(connector => {
          const statusConfig = STATUS_CONFIG[connector.status];
          const StatusIcon = statusConfig.icon;
          const TypeIcon = TYPE_ICONS[connector.type] || Database;
          
          return (
            <div 
              key={connector.id} 
              className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5 hover:bg-[#151515] transition-all"
              data-testid={`connector-${connector.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-[#1F1F1F]">
                  <TypeIcon size={20} className="text-[#6B6B6B]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-white">{connector.name}</h3>
                    <span className="px-2 py-0.5 text-[10px] font-medium bg-[#1F1F1F] text-[#A0A0A0] rounded">
                      {connector.type}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded flex items-center gap-1 ${statusConfig.bg} ${statusConfig.text}`}>
                      <StatusIcon size={10} />
                      {statusConfig.label}
                    </span>
                  </div>
                  <p className="text-sm text-[#6B6B6B]">{connector.description}</p>
                </div>
                <div className="text-right">
                  {connector.lastSync ? (
                    <>
                      <div className="text-sm font-medium text-white">{connector.dataPoints.toLocaleString()} points</div>
                      <div className="text-xs text-[#6B6B6B]">
                        Last sync: {new Date(connector.lastSync).toLocaleString()}
                      </div>
                    </>
                  ) : (
                    <span className="text-xs text-[#6B6B6B]">Not synced</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors">
                    <RefreshCw size={14} />
                  </button>
                  <button className="p-2 text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors">
                    <Settings size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
